import { useMemo } from "react";
import { useFestivalTheme } from "@/context/FestivalThemeContext";

/**
 * PAQVERO After Dark — ambient sparkle field.
 * A small number of tiny sparks that fade in/out on independent slow cycles
 * (7–12s), rendered behind page content (z-0). Dark theme only (CSS hides
 * the layer in light mode); animation is disabled under prefers-reduced-motion.
 * Color/count/enabled can be overridden by the active festival theme.
 */
export default function Sparkles() {
  const { sparkles } = useFestivalTheme();
  const count = sparkles ? Math.min(24, Math.max(0, sparkles.count ?? 14)) : 14;
  const color = sparkles?.color || "#FFC964";
  const enabled = sparkles ? sparkles.enabled !== false && count > 0 : true;

  const particles = useMemo(
    () =>
      Array.from({ length: count }, (_, i) => ({
        id: i,
        // Bias half of the field toward the hero/upper regions, the rest anywhere.
        top: i % 2 === 0 ? 4 + Math.random() * 42 : 4 + Math.random() * 92,
        left: 2 + Math.random() * 96,
        size: [1, 1.5, 2, 2, 2.5, 3][i % 6],
        opacity: 0.25 + Math.random() * 0.3,
        duration: 7 + Math.random() * 5,
        delay: Math.random() * 12,
      })),
    [count]
  );

  if (!enabled) return null;

  return (
    <div className="pq-sparkles" aria-hidden="true" data-testid="ambient-sparkles">
      {particles.map((p) => (
        <span
          key={p.id}
          className="pq-sparkle"
          style={{
            top: `${p.top}%`,
            left: `${p.left}%`,
            width: p.size,
            height: p.size,
            background: color,
            color: color,
            "--s-op": p.opacity,
            "--s-dur": `${p.duration}s`,
            "--s-delay": `${p.delay}s`,
          }}
        />
      ))}
    </div>
  );
}
